<template>
    <ui-dropdown placement="bottom-start" :menu-class="menuClass" trigger-class="flex-1">
        <template #default="dropdownScope">
            <slot v-bind="scope(dropdownScope)"></slot>
        </template>

        <template #menu="{ close }">
            <div class="p-3 max-h-64 overflow-y-auto w-full">

                <!-- Empty State. -->
                <div v-if="!query" class="text-gray-500 text-sm">
                    Start typing to search for known materials...
                </div>
                <!-- Results -->
                <div v-else class="space-y-6">
                    <div v-if="materials.length > 0">
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Materials</div>
                        <div class="space-y-1">
                            <div
                                v-for="material in materials"
                                class="flex items-center space-x-2 text-left w-full rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                :title="material.name"
                            >
                                <div class="truncate flex-1 flex flex-col">
                                    <div class="truncate w-full">{{ material.name }}</div>
                                    <template v-for="supplier in material.suppliers">
                                        <div class="truncate text-xs text-blue-500 mx-3 pt-0.5 p-0.5"
                                             v-for="price in supplier.prices">
                                            <button
                                                @click="selectMaterial(material,supplier.name,price.purchaseCost, close)"
                                                class="flex px-1.5 py-0.5 -my-0.5 border border-transparent rounded-lg text-xs w-full text-left font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-blue-500 hover:text-gray-600"
                                                :class="price.priceAgreement?'text-red-500':'text-blue-500'"
                                            >
                                                <span class="flex-1">{{
                                                        supplier.name
                                                    }} &#x2022 {{ price.purchaseCost | toCurrency }}</span>
                                                <span v-if="price.priceAgreement" class="ml-5 text-red-300"><span
                                                    class="fas fa-star"
                                                    style="font-size: 8px"> </span> Supplier Quote</span>
                                                <span v-if="price.usedInEstimate" class="ml-5 text-blue-300"><span
                                                    class="fas fa-star" style="font-size: 8px"> </span> Used in Estimate</span>
                                            </button>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-gray-500 text-sm">
                        No matching materials found.
                    </div>
                </div>
            </div>
        </template>
    </ui-dropdown>
</template>

<script>

import {cloneDeep} from "lodash";
import {toCurrency} from "../../../../utils";

export default {
    props: {
        top: Boolean,
        buttonClass: {
            type: String,
            default: 'text-left w-full rounded-md px-2 py-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600',
        },
        menuClass: {
            type: String,
            default: 'w-full',
        },
        store: Object,
        estimate: Object,
    },
    data() {
        return {
            query: '',
            materials: [],
        }
    },
    computed: {},
    methods: {
        async searchMaterials() {
            if (this.store.loaded) {
                if (!this.store.materialsProcessed || (this.store.materialsProcessed !== this.estimate.collectionBuildUUID)) {
                    let processedMaterials = cloneDeep(this.store.materials);
                    if (this.store.supplierQuotes) {
                        this.store.supplierQuotes.forEach((sq) => {
                            sq.items.forEach((item) => {
                                if (item.type === 'Material') {
                                    let material = processedMaterials.find((i) => i.name === item.name)
                                    if (!material) {
                                        material = {name: item.name, unitOfMeasure: item.unitOfMeasure, suppliers: []}
                                        processedMaterials.unshift(material)
                                    }
                                    let supplier = material.suppliers.find((supplier) => supplier.name === sq.supplierName);
                                    if (!supplier) {
                                        supplier = {
                                            name: sq.supplierName,
                                            hasMPA: true,
                                            prices: []
                                        }
                                        material.suppliers.push(supplier)
                                    } else {
                                        supplier.hasMPA = true
                                    }
                                    supplier.prices.push({
                                        unitOfPurchase: item.unitOfMeasure,
                                        purchaseCost: toCurrency(parseFloat(item.costPerUnit.replace(/[^0-9.]/g, ''))),
                                        priceAgreement: true
                                    });
                                }
                            })
                        })
                    }
                    Array.from(this.estimate.collectedItems.materials, ([name, value]) => value).forEach((i) => {
                        let material = processedMaterials.find((item) => item.name === i.name)
                        if (!material) {
                            material = {name: i.name, unitOfMeasure: i.unitOfMeasure, suppliers: []}
                            processedMaterials.unshift(material)
                        }
                        let supplier = material.suppliers.find((supplier) => (supplier.name === i.supplier) && !supplier.hasMPA);
                        if (!supplier) {
                            supplier = {
                                name: i.supplier,
                                reference: null,
                                prices: [],
                                usedInEstimate: true,
                            }
                            material.suppliers.push(supplier)
                        } else {
                            supplier.usedInEstimate = true
                        }
                        let priceIndex = supplier.prices.findIndex((p) => toCurrency(p.purchaseCost) === toCurrency(i.cost));
                        if (priceIndex > -1) {
                            supplier.prices[priceIndex].usedInEstimate = true;
                        } else {
                            supplier.prices.unshift({
                                purchaseCost: toCurrency(i.cost),
                                usedInEstimate: true
                            })
                        }
                    })
                    this.store.materialsProcessed = this.estimate.collectionBuildUUID;
                    this.store.processedMaterials = processedMaterials;
                }
                this.materials = this.store.processedMaterials.filter(this.matchContact)
                    .sort((a, b) => {
                        let ascore = (a.suppliers.some((i) => i.usedInEstimate) ? 10 : 0) + (a.suppliers.some((i) => i.hasMPA) ? 1 : 0)
                        let bscore = (b.suppliers.some((i) => i.usedInEstimate) ? 10 : 0) + (b.suppliers.some((i) => i.hasMPA) ? 1 : 0)
                        return bscore - ascore
                    }).slice(0, 50)
                this.materials.forEach((material) => {
                    material.suppliers.sort((a, b) => {
                        let ascore = (a.usedInEstimate ? 10 : 0) + (a.hasMPA ? 1 : 0)
                        let bscore = (b.usedInEstimate ? 10 : 0) + (b.hasMPA ? 1 : 0)
                        return bscore - ascore
                    })
                })
            }
        },
        matchContact(material) {
            const query = this.query.toLowerCase().split(/[^A-Za-z0-9.-]+/);
            let ret = query.every((token) => {
                return (material.name).toLowerCase().includes(token) || (material.name && (material.suppliers.some((supplier) => (supplier.name).toLowerCase().includes(token))));
            });
            return ret;
        },
        scope(dropdownScope) {
            return {
                query: this.query,
                setQuery: this.setQuery,
                ...dropdownScope
            }
        },
        setQuery(query) {
            this.query = query
            this.searchMaterials();
        },
        selectMaterial(material, supplierName, purchaseCost, close) {
            this.$emit('selected:material', {material, supplierName, purchaseCost});
            this.query = material.name;
            this.estimate.buildCollectedItems()
            close();
        },
    },
}
</script>
